import type { Directive, DirectiveBinding } from 'vue'

interface MaskaHTMLElement extends HTMLInputElement {
  _maskaUnwatch?: () => void;
  _maskaOldValue?: string;
}

interface MaskaOptions {
  mask: string;
  tokens?: { [key: string]: RegExp | string };
}

const DEFAULT_TOKENS = {
  '#': /\d/,
  'X': /[0-9a-zA-Z]/,
  'S': /[a-zA-Z]/,
  'A': /[a-zA-Z]/,
  'a': /[a-zA-Z]/,
  '*': /[0-9a-zA-Z]/
}

function processMaska(value: string, mask: string, tokens: { [key: string]: RegExp | string } = DEFAULT_TOKENS): string {
  let result = '';
  let valueIndex = 0;

  for (let maskIndex = 0; maskIndex < mask.length && valueIndex < value.length; maskIndex++) {
    const maskChar = mask[maskIndex];
    const token = tokens[maskChar];

    if (!token) {
      // Если символ маски не является токеном - добавляем его как есть
      result += maskChar;
      continue;
    }

    while (valueIndex < value.length) {
      const valueChar = value[valueIndex];
      const regex = token instanceof RegExp ? token : new RegExp(token);

      if (regex.test(valueChar)) {
        result += valueChar;
        valueIndex++;
        break;
      }
      valueIndex++;
    }
  }

  return result;
}

function updateValue(el: HTMLElement, binding: DirectiveBinding): void {
  const input = (el instanceof HTMLInputElement ? el : el.querySelector('input')) as MaskaHTMLElement;

  if (input == null || input?.type === 'file') return

  const mask = binding.value || binding.arg || '';
  const tokens = typeof binding.modifiers === 'object' && Object.keys(binding.modifiers).length > 0 
    ? Object.fromEntries(Object.keys(binding.modifiers).map(key => [key, /./])) 
    : DEFAULT_TOKENS;

  const cursorPosition = input.selectionStart || 0;
  const oldValue = input._maskaOldValue || '';
  
  // Очищаем значение от символов маски
  const cleanValue = input.value.replace(/[^\w\s]/g, '');
  
  // Применяем маску
  const newValue = processMaska(cleanValue, mask, tokens);
  
  // Обновляем значение, если оно изменилось
  if (input.value !== newValue) {
    input.value = newValue;
    input._maskaOldValue = newValue;

    // Вычисляем новую позицию курсора
    let newPosition = cursorPosition;
    if (newValue.length > oldValue.length) {
      newPosition++;
    } else if (newValue.length < oldValue.length) {
      newPosition--;
    }
    
    // Устанавливаем курсор
    input.setSelectionRange(newPosition, newPosition);

    // Генерируем событие input для v-model
    input.dispatchEvent(new Event('input'));
  }
}

const getInput = (el: HTMLElement) => {
  const input = (el instanceof HTMLInputElement ? el : el.querySelector('input')) as MaskaHTMLElement;
  
  if (input == null || input?.type === 'file') {
    return null;
  }

  return input;
}

export const vMaska: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const input = getInput(el);

    if (input == null) {
      return;
    }

    const listener = () => updateValue(input, binding);
    input.addEventListener('input', listener);
    input._maskaUnwatch = () => el.removeEventListener('input', listener);

    // Инициализируем начальное значение
    updateValue(input, binding);
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    const input = getInput(el);

    if (input == null) {
      return;
    }

    updateValue(el, binding);
  },

  unmounted(el: HTMLElement) {
    const input = getInput(el);

    if (input == null) {
      return;
    }
    
    input._maskaUnwatch?.();
  }
}